const QUALITY = 0.7
export function compressImage(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = event => {
      const img = new Image()
      img.onload = () => {
        const canvas = document.createElement('canvas')

        canvas.width = img.width
        canvas.height = img.height

        canvas
          .getContext('2d')
          .drawImage(img, 0, 0, canvas.width, canvas.height)

        const format = file.type === 'image/png' ? 'image/png' : 'image/jpeg'
        canvas.toBlob(
          blob => {
            if (blob) {
              resolve({ compressedFile: blob, image: event.target.result })
            } else {
              reject(new Error('Compression failed.'))
            }
          },
          format,
          QUALITY
        )
      }
      img.onerror = () => reject(new Error('Failed to load the image.'))
      img.src = event.target.result
    }
    reader.onerror = () => reject(new Error('Failed to read the file.'))
    reader.readAsDataURL(file)
  })
}

export function imageUrlToBase64(url) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = img.width
      canvas.height = img.height

      const ctx = canvas.getContext('2d')
      ctx.drawImage(img, 0, 0)
      const mimeType = getMimeTypeFromUrl(url) || 'image/png'
      const dataURL = canvas.toDataURL(mimeType)
      resolve(dataURL.split(',')[1])
    }

    img.onerror = err =>
      reject(new Error('Error al cargar la imagen: ' + err.message))
    img.src = `${url}?v=${new Date().getTime()}`
  })
}

const mimeTypes = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',
  svg: 'image/svg+xml',
  pdf: 'application/pdf',
  txt: 'text/plain',
  html: 'text/html',
  css: 'text/css',
  js: 'application/javascript',
  json: 'application/json',
  mp4: 'video/mp4',
  mp3: 'audio/mpeg',
  wav: 'audio/wav',
  zip: 'application/zip'
}
export function getMimeTypeFromUrl(url) {
  const extension = url.split('.').pop().toLowerCase()
  return mimeTypes[extension]
}

// Calibrated thresholds
export const MIN_FILE_SIZE = 10_000 // 10KB
export const MAX_FILE_SIZE = 10_000_000 // 10MB
export const MIN_RESOLUTION = 100 // px
export const MIN_CONTRAST = 10 // Minimum contrast
export const MIN_SHARPNESS = 13 // Minimum sharpness (detects blur)
export const WARN_SHARPNESS = 25 // Warning threshold

export const QUALITY_STATUS = {
  OK: 'OK',
  WARN: 'WARN',
  REJECT: 'REJECT'
}
export const REJECT_REASONS = {
  LOW_RESOLUTION: 'LOW_RESOLUTION',
  MIN_FILE_SIZE: 'MIN_FILE_SIZE',
  MAX_FILE_SIZE: 'MAX_FILE_SIZE'
}

/**
 * Checks image quality
 * @param {string} imageDataUrl
 * @param {Blob} file
 * @returns {Promise<{status: 'OK'|'WARN'|'REJECT', fileSizeOK: boolean, contrastOK: boolean, sharpnessOK: boolean, details: object}>}
 */
export function checkImageQuality(imageDataUrl, file) {
  return new Promise((resolve, reject) => {
    // 1. Fast check
    const fileSizeOK = file.size >= MIN_FILE_SIZE && file.size <= MAX_FILE_SIZE

    if (!fileSizeOK) {
      return resolve({
        status: QUALITY_STATUS.REJECT,
        fileSizeOK: false,
        contrastOK: false,
        sharpnessOK: false,
        details: {
          fileSize: file.size,
          reason:
            file.size < MIN_FILE_SIZE
              ? REJECT_REASONS.MIN_FILE_SIZE
              : REJECT_REASONS.MAX_FILE_SIZE
        }
      })
    }

    // 2. Check image quality
    const img = new Image()

    img.onload = () => {
      try {
        const resOK = Math.max(img.width, img.height) >= MIN_RESOLUTION

        if (!resOK) {
          return resolve({
            status: QUALITY_STATUS.REJECT,
            fileSizeOK: true,
            contrastOK: false,
            sharpnessOK: false,
            details: {
              width: img.width,
              height: img.height,
              reason: REJECT_REASONS.LOW_RESOLUTION
            }
          })
        }

        // 3.Contrast analysis on reduced sample (256x256)
        const SAMPLE_SIZE = 256
        const canvas = document.createElement('canvas')
        canvas.width = SAMPLE_SIZE
        canvas.height = SAMPLE_SIZE

        const ctx = canvas.getContext('2d', { willReadFrequently: true })
        ctx.drawImage(img, 0, 0, SAMPLE_SIZE, SAMPLE_SIZE)

        const imageData = ctx.getImageData(0, 0, SAMPLE_SIZE, SAMPLE_SIZE)
        const data = imageData.data

        // Convert to grayscale for analysis
        const grayPixels = []
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i]
          const g = data[i + 1]
          const b = data[i + 2]
          // Perceived luminance (ITU-R BT.709)
          const gray = 0.2126 * r + 0.7152 * g + 0.0722 * b
          grayPixels.push(gray)
        }

        // 1. Contrast analysis (standard deviation)
        let sum = 0
        let sum2 = 0
        for (let i = 0; i < grayPixels.length; i++) {
          sum += grayPixels[i]
          sum2 += grayPixels[i] * grayPixels[i]
        }
        const mean = sum / grayPixels.length
        const variance = sum2 / grayPixels.length - mean * mean
        const stdDev = Math.sqrt(variance)

        // 2. Sharpness analysis (Laplacian - detects edges/blur)
        let laplacianSum = 0
        let laplacianCount = 0

        // Apply simplified Laplacian operator (3x3 kernel)
        for (let y = 1; y < SAMPLE_SIZE - 1; y++) {
          for (let x = 1; x < SAMPLE_SIZE - 1; x++) {
            const idx = y * SAMPLE_SIZE + x
            const center = grayPixels[idx]
            const top = grayPixels[idx - SAMPLE_SIZE]
            const bottom = grayPixels[idx + SAMPLE_SIZE]
            const left = grayPixels[idx - 1]
            const right = grayPixels[idx + 1]

            // Laplacian: difference of center with neighbors
            const laplacian = Math.abs(4 * center - top - bottom - left - right)
            laplacianSum += laplacian * laplacian
            laplacianCount++
          }
        }

        const laplacianVariance = laplacianSum / laplacianCount
        const sharpness = Math.sqrt(laplacianVariance)

        const contrastOK = stdDev >= MIN_CONTRAST
        const sharpnessOK = sharpness >= MIN_SHARPNESS

        let status
        if (!contrastOK || !sharpnessOK) {
          status = QUALITY_STATUS.REJECT
        } else if (sharpness < WARN_SHARPNESS) {
          status = QUALITY_STATUS.WARN
        } else {
          status = QUALITY_STATUS.OK
        }

        resolve({
          status,
          fileSizeOK,
          contrastOK,
          sharpnessOK,
          details: {
            width: img.width,
            height: img.height,
            fileSize: file.size,
            contrast: Math.round(stdDev),
            sharpness: Math.round(sharpness),
            brightness: Math.round(mean)
          }
        })
      } catch (error) {
        reject(new Error('Error al analizar la imagen: ' + error.message))
      }
    }

    img.onerror = () => reject(new Error('No se pudo cargar la imagen'))
    img.src = imageDataUrl
  })
}
